import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import analyticsService from "../AnalyticsService";
import { useSelector } from "react-redux";
import { resolvePageName } from "./resolvePageName";

export const usePageTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const lastTrackedRouteKey = useRef(null);
  const currentDashboard = useSelector(
    (state) => state?.dashboardReducer?.currentDashboard,
  );
  const selectedCard = useSelector(
    (state) => state?.dashboardReducer?.selectedCard,
  );

  const currentPath = `${location?.pathname}${location?.search || ""}`;
  const currentDashboardTitle =
    currentDashboard?.CONFIG?.pageHeader?.pageTitle || null;
  const selectedCardName =
    currentDashboard?.KEY === 2
      ? selectedCard?.commercial_title
      : selectedCard?.retail_title;
  const trackingRouteKey = `${currentPath}|${currentDashboardTitle ?? ""}|${selectedCardName ?? ""}`;

  useEffect(() => {
    if (lastTrackedRouteKey.current === trackingRouteKey) {
      return undefined;
    }

    let timeoutId = null;
    let loadHandler = null;

    const runTracking = () => {
      lastTrackedRouteKey.current = trackingRouteKey;

      const pageName = resolvePageName(currentPath);
      const pageViewPayload = {
        page_name: pageName || document?.title,
        page_path: currentPath,
        navigation_type: navigationType,
        dashboard: currentDashboardTitle,
        navigated_from_card: selectedCardName || null,
      };

      analyticsService.trackPageView(pageViewPayload);
    };

    const scheduleTracking = () => {
      if (document.readyState === "complete") {
        timeoutId = window.setTimeout(runTracking, 150);
        return;
      }

      loadHandler = () => {
        timeoutId = window.setTimeout(runTracking, 150);
      };

      window.addEventListener("load", loadHandler, { once: true });
    };

    scheduleTracking();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      if (loadHandler) {
        window.removeEventListener("load", loadHandler);
      }
    };
  }, [
    trackingRouteKey,
    currentPath,
    navigationType,
    currentDashboardTitle,
    selectedCardName,
  ]);
};
